import { useEffect, useSyncExternalStore } from 'react'

/**
 * Session model for the persistent multi-campaign tab strip — Epic 48 Phase 02C
 * (`campaign-tabs-strip`), `DESIGN-HANDOVER.md` §8.2, owner decision D-TABS-1 (2026-08-25).
 *
 * D-TABS-1 settles the mechanic deliberately: browser-like, entirely client-side.
 *   - Opening a campaign is what adds its tab — there is no separate "open a tab" gesture.
 *   - A close removes a tab from the open set only; the campaign itself is never touched.
 *   - The open set persists in `localStorage`, **session-scoped to the browser** — not
 *     brand-scoped, not synced across devices or tabs-of-tabs. Two campaigns from different
 *     brands may validly sit open together.
 *   - No backend, no new DB table (`DATABASE-BRIEF.md` deliberately models none of this),
 *     no API call: this module never fetches a campaign's name. It only records whatever
 *     `{ id, name }` a caller already has (e.g. a workspace page's own campaign load) —
 *     see `TabStrip.tsx`'s own header for how a screen is expected to supply that.
 *
 * NOT this module's job: the keyboard/ARIA interaction model is a separate, still-OPEN
 * decision (`DESIGN-HANDOVER.md:767-771`, phase task 2C.6) — this file carries session
 * state only.
 *
 * --- Epic 48 Phase 02C lift: from per-component state to a MODULE-LEVEL STORE ---
 *
 * Until this lift, the "open set" lived in a plain `useState` inside `useOpenCampaignTabs`,
 * and `TabStrip` was mounted (and read that state) from exactly one place: `WorkspacePage`,
 * which also happened to be the one screen that KNOWS a campaign's name. §8.2 requires the
 * strip to sit "above every screen the workspace shell wraps" — `/dashboard`, `/brands`,
 * `/trash`, `/admin` included — none of which have a campaign at all. That means the strip
 * now has to mount in `WorkspaceAppShell.tsx` (which wraps ALL of those routes, has no
 * campaign, and passes no `current`), while `WorkspacePage` remains the only place that DOES
 * know the campaign's name.
 *
 * Two call sites, one piece of state, and each holding its own private `useState` would mean
 * `WorkspacePage` registering a campaign could never reach the shell's strip — they would be
 * two independent copies of "the open set" that happen to both read/write the same
 * `localStorage` key on their own schedule, not one shared source of truth. This module fixes
 * that the same way `lib/regenerate-store.ts` already fixed the identical "two components,
 * one truth" problem for regenerate-job state: hoist the array to MODULE scope, keep a
 * `Set` of listener callbacks, and expose it to React via `useSyncExternalStore` so every
 * subscriber re-renders on the same write. See that file's own header for the shape this
 * follows.
 *
 * --- Epic 49 (2026-08-30): capped open set, least-recently-used eviction ---
 *
 * The owner opened the running app next to the design and found the strip "chaos" — six tabs
 * crowding a 40px row meant to hold a small, clean number, including throwaway names like
 * `design-verify-2026-08-29-1788017140469` that never went away. The strip itself already
 * matched the node (single row, horizontal scroll, no overflow menu — D-TABS-1 rules that out);
 * the actual defect was here: this module recorded every campaign ever opened, for the life of
 * the browser's `localStorage`, and nothing ever removed one short of the still-dormant manual
 * `closeTab` (see `TabStrip.tsx`'s header for why that has no UI affordance yet).
 *
 * Fix: `MAX_OPEN_TABS` caps the open set. `registerTab` doubles as the only "this tab was just
 * used" signal this module gets (D-TABS-1 — opening/switching-to a tab is the one gesture), so
 * it also drives eviction order — a separate in-memory `lastUsedAt` recency map, NOT the
 * display array itself, so switching between already-open tabs does not visually reorder the
 * strip (that would read as its own kind of chaos; real browser tabs hold still when revisited).
 * When registering would push the set past the cap, the tab with the OLDEST recency touch is
 * evicted — never the one just registered, which `registerTab` always touches first, so the
 * campaign the user is presently looking at can never be evicted out from under them.
 *
 * The cap applies on READ too (`readTabs`), not just on write: a session that already has more
 * than `MAX_OPEN_TABS` recorded from before this fix shipped (or from any other pre-cap growth)
 * is trimmed the moment it's loaded, keeping the most-recently-opened tail — and the trim is
 * persisted immediately, so an idle session that never opens another campaign again doesn't
 * carry the overflow forever.
 */

const STORAGE_KEY = 'bainner_open_campaign_tabs'

/**
 * The open set never exceeds this many campaigns — least-recently-used eviction (see the
 * module header) keeps the strip a clean, bounded single row instead of growing without limit.
 * Chosen to match the size already proven, by `TabStrip.test.tsx`'s own "many open tabs" case,
 * to sit comfortably in the strip's horizontal-scroll container (D-TABS-1 rules out an overflow
 * menu, so the cap's job is only to stop unbounded growth, not to keep everything on-screen at
 * once).
 */
export const MAX_OPEN_TABS = 8

export interface OpenCampaignTab {
  id: string
  name: string
}

function isOpenCampaignTab(value: unknown): value is OpenCampaignTab {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { id?: unknown }).id === 'string' &&
    typeof (value as { name?: unknown }).name === 'string'
  )
}

/**
 * `localStorage` can throw outright (private browsing, blocked site data) — every read and
 * write is wrapped and degrades to "nothing was open" / "the change wasn't remembered", never
 * a crash. See `lib/active-batch-store.ts` for the same pattern already established here.
 */
function readTabs(): OpenCampaignTab[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    const valid = parsed.filter(isOpenCampaignTab)
    if (valid.length <= MAX_OPEN_TABS) return valid
    // Pre-existing overflow — recorded before this cap shipped, or otherwise corrupted past the
    // limit. There's no recency data for entries we never tracked, so the best available signal
    // is array position itself (oldest→newest — see `commit`): keep the most-recently-opened
    // tail and persist the trim right away, so it doesn't linger until the next mutation.
    const trimmed = valid.slice(-MAX_OPEN_TABS)
    writeTabs(trimmed)
    return trimmed
  } catch {
    return []
  }
}

function writeTabs(next: OpenCampaignTab[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch {
    /* best-effort — an unpersisted open set degrades to "forgotten on reload", never a crash */
  }
}

// ---------------------------------------------------------------------------
// The module-level store. `tabs` is read from `localStorage` exactly once, at module
// load (a real page load re-evaluates this module, so that IS "reload reads it back") —
// after that, this array is the single in-memory source of truth every subscriber reads;
// `localStorage` is only ever written back TO, never re-read, by the writes below.
// ---------------------------------------------------------------------------

let tabs: OpenCampaignTab[] = readTabs()
const listeners = new Set<() => void>()

/**
 * Recency, for eviction only — deliberately NOT part of `tabs`/`localStorage`. A monotonically
 * increasing counter per touched tab id; higher = more recently used. Kept separate from the
 * display array so switching to an already-open tab (a `registerTab` call) doesn't reorder what
 * the strip shows — only which tab is the next eviction candidate when the cap is exceeded.
 * Session-only by design: a fresh page load re-seeds it from `tabs`' own order (oldest first),
 * which is the only recency signal a reload actually has.
 */
const lastUsedAt = new Map<string, number>()
let recencyCounter = 0

/** Marks `id` as the most-recently-used tab. Always called before any eviction decision below,
 * so the tab just touched can never be the one evicted — see the module header. */
function touch(id: string): number {
  recencyCounter += 1
  lastUsedAt.set(id, recencyCounter)
  return recencyCounter
}

function seedRecency(list: OpenCampaignTab[]): void {
  lastUsedAt.clear()
  recencyCounter = 0
  for (const t of list) touch(t.id)
}
seedRecency(tabs)

function notify(): void {
  for (const cb of listeners) cb()
}

/**
 * Replaces the cached array with a NEW reference, persists it, and notifies. Every mutation
 * (`registerTab`, `closeTab`) goes through this — it is the ONLY place `tabs` is reassigned,
 * so `getSnapshot` below can hand back the same reference across any number of calls between
 * two `commit`s. `useSyncExternalStore` re-renders a subscriber whenever `getSnapshot`'s
 * return value differs by reference; committing only on an actual change (both callers below
 * already check that before calling this) is what keeps that from looping.
 */
function commit(next: OpenCampaignTab[]): void {
  tabs = next
  writeTabs(next)
  notify()
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

function getSnapshot(): OpenCampaignTab[] {
  return tabs
}

/** No real SSR in this Vite SPA, but `useSyncExternalStore`'s third argument still wants a
 * pure, stable value to hand back before hydration — an empty open set is the honest one. */
const EMPTY_TABS: OpenCampaignTab[] = []
function getServerSnapshot(): OpenCampaignTab[] {
  return EMPTY_TABS
}

/**
 * Registers `tab` as open — the ONLY "open a tab" gesture (D-TABS-1), and also the only
 * "switch to an already-open tab" signal this module gets, since that's what a re-render with
 * a different/unchanged `current` looks like from here. Both cases mark `tab` most-recently-used
 * (see `touch`) before anything else happens, so it can never be the tab evicted below.
 *
 * No display change when the campaign is already open under the same name — beyond the recency
 * touch, which never shows up in `tabs` itself. If it's open under a DIFFERENT name (a rename
 * since it was opened), the stored name is refreshed in place, position unchanged. A brand-new
 * campaign is appended at the end.
 *
 * When registering would push the open set past `MAX_OPEN_TABS`, the least-recently-used tab
 * (by `lastUsedAt`, excluding `tab.id` itself) is evicted — this is the fix for the open set
 * growing without bound (Epic 49). Only the append path can ever exceed the cap: the no-op and
 * rename paths both preserve `tabs.length`, which the cap already holds as an invariant after
 * every prior commit.
 */
function registerTab(tab: OpenCampaignTab): void {
  touch(tab.id)

  const idx = tabs.findIndex((t) => t.id === tab.id)
  let next: OpenCampaignTab[]
  if (idx === -1) {
    next = [...tabs, tab]
  } else if (tabs[idx]?.name !== tab.name) {
    next = tabs.map((t, i) => (i === idx ? { ...t, name: tab.name } : t))
  } else {
    next = tabs // nothing about the display array changed
  }

  while (next.length > MAX_OPEN_TABS) {
    // Least-recently-used wins eviction. Written as a reduce over the array itself rather than
    // seeding from `next[0]` so there is no index that TypeScript cannot prove exists — the
    // earlier version needed two non-null assertions to compile, which this file's lint config
    // forbids outright.
    //
    // `while`, not `if`: a persisted list read back from storage can arrive ALREADY longer than
    // the cap (the cap did not exist when it was written), and a single eviction would leave it
    // over the limit forever — the exact "tabs never go away" complaint this cap exists to fix.
    const evict = next.reduce((worst, t) =>
      (lastUsedAt.get(t.id) ?? -1) < (lastUsedAt.get(worst.id) ?? -1) ? t : worst,
    )
    next = next.filter((t) => t.id !== evict.id)
    lastUsedAt.delete(evict.id)
  }

  if (next === tabs) return // same reference — truly nothing to persist or notify about
  commit(next)
}

/** Removes a tab from the open set. Never touches the campaign it names. */
/**
 * The tab to land on after closing `id` — the one the user was on BEFORE, Chrome's rule
 * ("bir önceki olduğu taba dönecek", owner 2026-08-31).
 *
 * `lastUsedAt` already holds exactly this and was built for eviction: `registerTab` touches a tab
 * every time it is opened or switched to, so the highest recency among the OTHER tabs is by
 * definition the one visited most recently before this one. No new bookkeeping — the previous
 * guess (take the first tab in display order) had nothing to do with where the user had been.
 *
 * Returns null when nothing else is open; the caller decides where that goes.
 */
export function previousTabAfterClosing(id: string): OpenCampaignTab | null {
  let best: OpenCampaignTab | null = null
  let bestSeen = -1
  for (const t of tabs) {
    if (t.id === id) continue
    const seen = lastUsedAt.get(t.id) ?? -1
    if (seen > bestSeen) {
      bestSeen = seen
      best = t
    }
  }
  return best
}

function closeTab(id: string): void {
  const next = tabs.filter((t) => t.id !== id)
  if (next.length === tabs.length) return // nothing to do — keep the cached reference stable
  lastUsedAt.delete(id)
  commit(next)
}

/**
 * Shared effect body behind both hooks below: registers `current` on mount and whenever its
 * id/name change. Depends on the PRIMITIVE id/name, not `current`'s object identity — a
 * caller passing a fresh `{ id, name }` literal every render must not re-trigger this.
 */
function useRegisterCurrent(current: OpenCampaignTab | null | undefined): void {
  const currentId = current?.id
  const currentName = current?.name
  useEffect(() => {
    if (!currentId || currentName === undefined) return
    registerTab({ id: currentId, name: currentName })
  }, [currentId, currentName])
}

export interface UseOpenCampaignTabsResult {
  /**
   * Every campaign currently open in this browser session, oldest-opened first. Switching back
   * to an already-open tab does NOT move it — display position only changes when a tab is
   * newly opened (appended) or evicted (removed); see the module header. Capped at
   * `MAX_OPEN_TABS` with least-recently-used eviction — the campaign presently being viewed is
   * always exempt.
   */
  tabs: OpenCampaignTab[]
  /** Removes a tab from the open set. Never touches the campaign it names. */
  closeTab: (id: string) => void
}

/**
 * @param current The campaign the calling screen is presently showing, if any. Passing one
 *   IS the "open a tab" gesture — there is no separate one (D-TABS-1). Pass `null`/`undefined`
 *   on a screen that isn't scoped to one campaign (e.g. the campaign list); the already-open
 *   tabs are still returned unchanged. If `current`'s id is already open under a different
 *   name (a rename since it was opened), the stored name is refreshed to match.
 *
 *   `TabStrip` (the only caller today) always passes its OWN `current` prop straight through
 *   here — so a screen that mounts `TabStrip` directly still works exactly as before this
 *   lift. A screen that does NOT mount `TabStrip` itself (because the shell now mounts it
 *   once, above every route — see this file's header) should reach for
 *   `useRegisterCampaignTab` instead, so it registers into this SAME store without rendering
 *   a second strip.
 */
export function useOpenCampaignTabs(current?: OpenCampaignTab | null): UseOpenCampaignTabsResult {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
  useRegisterCurrent(current)
  return { tabs: snapshot, closeTab }
}

/**
 * Registers the campaign a screen is presently showing as an open tab, WITHOUT subscribing
 * to the open-tab list or rendering anything — for a screen that knows the live campaign
 * (like `WorkspacePage`) but does not itself mount `<TabStrip/>` (that now lives in
 * `WorkspaceAppShell`, which wraps every route and therefore has no campaign of its own —
 * see that file's header). Pass `null`/`undefined` while the campaign hasn't loaded yet
 * (or on a screen that isn't scoped to one at all); pass `{ id, name }` once it has.
 *
 * Why a hook and not a plain imperative function: the call site this replaces —
 * `<TabStrip current={...} />` — already re-ran on every id/name change via React's own
 * render cycle, and callers reach for this at the exact spot that render used to sit. A hook
 * keeps that "runs on mount, reacts to id/name changes, cleans up nothing because there is
 * nothing to clean up" behaviour explicit, and testable the same way — assert against the
 * shared store, e.g. via `useOpenCampaignTabs`'s own `tabs` — that `useOpenCampaignTabs`
 * itself already is.
 */
export function useRegisterCampaignTab(current: OpenCampaignTab | null | undefined): void {
  useRegisterCurrent(current)
}

/**
 * Test-only: forgets the in-memory cache and re-reads `localStorage` fresh, and drops every
 * listener. A real page load re-evaluates this module (so `tabs` is naturally re-derived);
 * a test file importing this module ONCE for many `it()`s has no such reload, so leftover
 * state from one test would otherwise leak into the next. Mirrors
 * `regenerateStore._resetForTest()` in `lib/regenerate-store.ts`. Call this in `beforeEach`,
 * AND again after any test seeds `localStorage` directly mid-test (this store does not
 * watch storage for external writes — see the header above) and before rendering against it.
 * Also re-seeds the recency map from the freshly-read `tabs` (see `seedRecency`), matching what
 * a real module reload does.
 */
export function _resetOpenCampaignTabsForTest(): void {
  tabs = readTabs()
  seedRecency(tabs)
  listeners.clear()
}
